import React, { useState, useEffect } from 'react';
import { contacts } from '../services/api';
import { toast } from 'react-toastify';

const ContactsTab = ({ loadMessages }) => {
  const [userContacts, setUserContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filteredContacts, setFilteredContacts] = useState([]);

  // Format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  // Load contacts
  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await contacts.list();
      if (response?.data) {
        setUserContacts(response.data);
        setFilteredContacts(response.data);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Filter contacts based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredContacts(userContacts);
      return;
    }

    const filtered = userContacts.filter(contact =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phoneNumber.includes(searchQuery) ||
      (contact.notes && contact.notes.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredContacts(filtered);
  }, [searchQuery, userContacts]);

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
  }, []);

  // Add Contact Modal
  const AddContactModal = () => {
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [notes, setNotes] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await contacts.create({
          name,
          phoneNumber,
          notes
        });
        await loadContacts();
        setShowAddModal(false);
        toast.success('Contact added successfully');
      } catch (error) {
        console.error('Error adding contact:', error);
        toast.error(error.response?.data?.message || 'Failed to add contact');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h2 className="text-xl font-semibold mb-4">Add New Contact</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                required
                placeholder="10-digit number"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                rows="3"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add Contact
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Edit Contact Modal
  const EditContactModal = () => {
    const [name, setName] = useState(selectedContact?.name || '');
    const [notes, setNotes] = useState(selectedContact?.notes || '');

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await contacts.update(selectedContact._id, {
          name,
          notes
        });
        await loadContacts();
        setShowEditModal(false);
        toast.success('Contact updated successfully');
      } catch (error) {
        console.error('Error updating contact:', error);
        toast.error(error.response?.data?.message || 'Failed to update contact');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h2 className="text-xl font-semibold mb-4">Edit Contact</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                type="text"
                value={formatPhoneNumber(selectedContact?.phoneNumber)}
                disabled
                className="w-full p-2 border rounded bg-gray-100"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                rows="3"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Delete Contact Modal
  const DeleteContactModal = () => {
    const handleDelete = async () => {
      try {
        await contacts.delete(selectedContact._id);
        await loadContacts();
        setShowDeleteModal(false);
        toast.success('Contact deleted successfully');
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h3 className="text-xl font-semibold mb-4">Delete Contact</h3>
          <p className="mb-6">
            Are you sure you want to delete {selectedContact?.name}? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 bg-[#f0f2f5] border-b">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-[#41525d]">Contacts</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72] focus:outline-none"
          >
            Add Contact
          </button>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 pl-10 border rounded-lg focus:outline-none focus:border-[#00a884]"
          />
          <svg
            className="absolute left-3 top-3 h-4 w-4 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Contacts List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00a884]"></div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            <div className="text-6xl mb-4">👥</div>
            <p>{searchQuery ? 'No contacts found' : 'No contacts yet'}</p>
            {!searchQuery && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 text-[#00a884] hover:underline"
              >
                Add your first contact
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {filteredContacts.map((contact) => (
              <div
                key={contact._id}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div>
                  <h3 className="font-medium">{contact.name}</h3>
                  <p className="text-sm text-gray-500">{formatPhoneNumber(contact.phoneNumber)}</p>
                  {contact.notes && (
                    <p className="text-sm text-gray-500 mt-1">{contact.notes}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => loadMessages(contact.phoneNumber)}
                    className="p-2 text-gray-600 hover:text-[#00a884] rounded-full hover:bg-gray-100"
                    title="Start chat"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedContact(contact);
                      setShowEditModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-500 rounded-full hover:bg-gray-100"
                    title="Edit contact"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedContact(contact);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-red-500 rounded-full hover:bg-gray-100"
                    title="Delete contact"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && <AddContactModal />}
      {showEditModal && <EditContactModal />}
      {showDeleteModal && <DeleteContactModal />}
    </div>
  );
};

export default ContactsTab;
